import { useEffect, useState } from 'react'

export type VisitorLocation = {
  date: string
  city: string | null
  countryCode: string | null
  resolved: boolean
}

const formatDate = (timeZone?: string): string => {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
  try {
    return new Intl.DateTimeFormat('es-ES', { ...options, ...(timeZone ? { timeZone } : {}) }).format(
      new Date(),
    )
  } catch {
    return new Intl.DateTimeFormat('es-ES', options).format(new Date())
  }
}

type IpResponse = {
  city?: string
  country_code?: string
  timezone?: string
}

/**
 * Resolves the visitor's city, country and local date from their IP address.
 * Falls back to the browser date and a generic byline if the lookup fails.
 */
export function useVisitorLocation(): VisitorLocation {
  const [location, setLocation] = useState<VisitorLocation>(() => ({
    date: formatDate(),
    city: null,
    countryCode: null,
    resolved: false,
  }))

  useEffect(() => {
    let cancelled = false

    const resolve = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/')
        if (!response.ok) throw new Error('lookup failed')
        const data: IpResponse = await response.json()
        if (cancelled) return
        setLocation({
          date: formatDate(data.timezone),
          city: data.city ?? null,
          countryCode: data.country_code ?? null,
          resolved: true,
        })
      } catch {
        if (!cancelled) setLocation((prev) => ({ ...prev, resolved: true }))
      }
    }

    resolve()
    return () => {
      cancelled = true
    }
  }, [])

  return location
}
